import { useState, useEffect } from "react";
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import AppShell, { ACCENT, ACCENT_DEEP, ACCENT_SOFT } from "../components/AppShell";
import { API_BASE_URL } from '@/utils/env';

type PersonRole = 'shareholder' | 'ubo' | 'director' | 'signatory';

interface Person {
  id: string;
  role: PersonRole;
  name: string;
  nationality: string;
  ownershipPct: string;
  isUBO: boolean;
}

interface TradeLicense {
  number: string;
  authority: string;
  issueDate: string;
  expiryDate: string;
}

interface CompanyData {
  legalName: string;
  tradingName: string;
  website: string;
  industry: string;
  sector: string;
  registeredAddress: string;
  description: string;
  tradeLicense: TradeLicense;
  people: Person[];
}

const EMPTY_COMPANY: CompanyData = {
  legalName: '', tradingName: '', website: '', industry: '', sector: '',
  registeredAddress: '', description: '',
  tradeLicense: { number: '', authority: '', issueDate: '', expiryDate: '' },
  people: [],
};

type Tab = 'details' | 'licence' | 'people';

const ROLE_LABEL: Record<PersonRole, string> = {
  shareholder: 'Shareholder',
  ubo: 'UBO',
  director: 'Director',
  signatory: 'Signatory',
};

const ROLE_COLOR: Record<PersonRole, string> = {
  shareholder: '#1976d2',
  ubo: '#7c3aed',
  director: '#059669',
  signatory: '#d97706',
};

function expiryMessage(dateStr: string): { text: string; color: string } | null {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(dateStr);
  const days = Math.round((exp.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { text: `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago.`, color: 'text-red-600' };
  if (days === 0) return { text: 'Expires today.', color: 'text-amber-600' };
  if (days <= 30) return { text: `Expires in ${days} day${days !== 1 ? 's' : ''}. Renewal reminders will be sent.`, color: 'text-amber-600' };
  return { text: 'Valid · Reminders will be sent 90, 30, 14, 7 and 1 days before expiry.', color: 'text-green-600' };
}

export default function CompanyProfile() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('details');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState<CompanyData>(EMPTY_COMPANY);
  const [peopleRole, setPeopleRole] = useState<PersonRole>('shareholder');

  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) { navigate('/login'); return; }
        const user = JSON.parse(userStr);
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) }
        });
        if (resp.ok) {
          const json = await resp.json();
          if (json.success && json.user?.company) {
            setData({ ...EMPTY_COMPANY, ...json.user.company });
          }
        }
      } catch (e) { console.error(e); }
      finally { setFetching(false); }
    })();
  }, [navigate]);

  const setField = (field: keyof Omit<CompanyData, 'tradeLicense' | 'people'>, value: string) =>
    setData(prev => ({ ...prev, [field]: value }));

  const setLic = (field: keyof TradeLicense, value: string) =>
    setData(prev => ({ ...prev, tradeLicense: { ...prev.tradeLicense, [field]: value } }));

  const addPerson = () => {
    const p: Person = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      role: peopleRole, name: '', nationality: '', ownershipPct: '', isUBO: false,
    };
    setData(prev => ({ ...prev, people: [...prev.people, p] }));
  };

  const updatePerson = (id: string, field: keyof Person, value: any) =>
    setData(prev => ({ ...prev, people: prev.people.map(p => p.id === id ? { ...p, [field]: value } : p) }));

  const removePerson = (id: string) =>
    setData(prev => ({ ...prev, people: prev.people.filter(p => p.id !== id) }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/login'); return; }
      const user = JSON.parse(userStr);
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ company: { ...data, updatedAt: new Date().toISOString() } })
      });
      if (!resp.ok) throw new Error('Failed to save');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
      showAlert('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => { localStorage.removeItem('user'); navigate('/'); };

  const filteredPeople = data.people.filter(p => p.role === peopleRole);
  const expiry = expiryMessage(data.tradeLicense.expiryDate);

  const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none";

  if (fetching) return <AppShell title="Company" crumb="Company" onLogout={handleSignOut} loading />;

  return (
    <AppShell
      title="Company"
      subtitle="Your business record: identity, licence and ownership. This anchors everything across Conncct."
      crumb="Company"
      onLogout={handleSignOut}
    >
      <div className="pb-24">

          {/* Tabs */}
          <div className="flex gap-1 bg-white/70 border border-gray-200 rounded-lg p-1 mb-6 w-fit">
            {(['details', 'licence', 'people'] as Tab[]).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className="px-4 py-2 text-sm font-medium rounded-md transition"
                style={tab === t
                  ? { background: ACCENT_SOFT, color: ACCENT_DEEP }
                  : { color: '#6B7280' }}>
                {t === 'details' ? 'Details' : t === 'licence' ? 'Licence & Registration' : 'People'}
              </button>
            ))}
          </div>

          {/* Details tab */}
          {tab === 'details' && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="text-sm font-semibold text-[#111827]">Company details</div>
                <div className="text-xs text-[#6B7280]">Business identity</div>
              </div>
              <div className="px-6 py-6">
                {/* Three across on wide screens: the card now spans the full shell,
                    and a company-name field 1300px wide helps nobody. Address and
                    description still span, since they take real prose. */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Legal company name</label>
                    <input type="text" value={data.legalName} onChange={e => setField('legalName', e.target.value)} placeholder="As per trade licence" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Trading name (optional)</label>
                    <input type="text" value={data.tradingName} onChange={e => setField('tradingName', e.target.value)} placeholder="Brand / operating name" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Website (optional)</label>
                    <input type="url" value={data.website} onChange={e => setField('website', e.target.value)} placeholder="https://" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Industry</label>
                    <input type="text" value={data.industry} onChange={e => setField('industry', e.target.value)} placeholder="e.g. Technology, Retail, F&B" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Sector / sub-sector (optional)</label>
                    <input type="text" value={data.sector} onChange={e => setField('sector', e.target.value)} placeholder="e.g. SaaS / Fintech" className={inputCls} />
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Registered address</label>
                    <input type="text" value={data.registeredAddress} onChange={e => setField('registeredAddress', e.target.value)} placeholder="Office / registered address" className={inputCls} />
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Business description (optional)</label>
                    <textarea value={data.description} onChange={e => setField('description', e.target.value)} placeholder="What your company does, in a few lines" rows={3}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none acc-ring focus:outline-none" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Licence tab */}
          {tab === 'licence' && (
            <div>
              <div className="acc-soft-bg acc-line acc-ink px-5 py-3.5 border rounded-xl mb-4 text-sm">
                Your trade licence drives renewal reminders and compliance tracking across Conncct.
              </div>
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="text-sm font-semibold text-[#111827]">Trade licence</div>
                  <div className="text-xs text-[#6B7280]">Drives renewal reminders</div>
                </div>
                <div className="px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1.5">Trade licence number</label>
                      <input type="text" value={data.tradeLicense.number} onChange={e => setLic('number', e.target.value)} placeholder="Licence number" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1.5">Licensing authority</label>
                      <input type="text" value={data.tradeLicense.authority} onChange={e => setLic('authority', e.target.value)} placeholder="e.g. DED Dubai, DMCC, ADGM" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1.5">Licence issue date</label>
                      <input type="date" value={data.tradeLicense.issueDate} onChange={e => setLic('issueDate', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1.5">Licence expiry date</label>
                      <input type="date" value={data.tradeLicense.expiryDate} onChange={e => setLic('expiryDate', e.target.value)} className={inputCls} />
                      {expiry && <p className={`mt-1.5 text-xs ${expiry.color}`}>{expiry.text}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* People tab */}
          {tab === 'people' && (
            <div>
              <div className="acc-soft-bg acc-line acc-ink px-5 py-3.5 border rounded-xl mb-4 text-sm">
                Add shareholders, UBOs, directors and authorised signatories for AML / due-diligence.
              </div>
              <div className="bg-white rounded-xl border border-gray-200">
                {/* Role sub-tabs */}
                <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap gap-2">
                  {(['shareholder', 'ubo', 'director', 'signatory'] as PersonRole[]).map(r => (
                    <button key={r} onClick={() => setPeopleRole(r)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition border ${peopleRole === r ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                      style={peopleRole === r ? { background: ROLE_COLOR[r], borderColor: ROLE_COLOR[r] } : {}}>
                      {ROLE_LABEL[r]}s
                    </button>
                  ))}
                </div>

                <div className="px-6 py-4 space-y-4">
                  {filteredPeople.length === 0 && (
                    <p className="text-sm text-[#6B7280] text-center py-4">
                      No {ROLE_LABEL[peopleRole].toLowerCase()}s added yet.
                    </p>
                  )}

                  {filteredPeople.map((person, idx) => (
                    <div key={person.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="px-2 py-0.5 text-xs font-semibold rounded uppercase"
                          style={{ background: `${ROLE_COLOR[person.role]}18`, color: ROLE_COLOR[person.role] }}>
                          {ROLE_LABEL[person.role]} {idx + 1}
                        </span>
                        <button onClick={() => removePerson(person.id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-[#374151] mb-1">Full name</label>
                          <input type="text" value={person.name} onChange={e => updatePerson(person.id, 'name', e.target.value)} placeholder="Full legal name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#374151] mb-1">Nationality</label>
                          <input type="text" value={person.nationality} onChange={e => updatePerson(person.id, 'nationality', e.target.value)} placeholder="Nationality"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                        </div>
                        {(person.role === 'shareholder' || person.role === 'ubo') && (
                          <div>
                            <label className="block text-xs font-medium text-[#374151] mb-1">Ownership %</label>
                            <input type="number" min="0" max="100" value={person.ownershipPct} onChange={e => updatePerson(person.id, 'ownershipPct', e.target.value)} placeholder="0–100"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                          </div>
                        )}
                        {person.role === 'shareholder' && (
                          <div className="flex items-center gap-2 self-end pb-2">
                            <input type="checkbox" id={`ubo-${person.id}`} checked={person.isUBO}
                              onChange={e => updatePerson(person.id, 'isUBO', e.target.checked)}
                              className="w-4 h-4 rounded" style={{ accentColor: ACCENT }} />
                            <label htmlFor={`ubo-${person.id}`} className="text-xs text-[#374151]">Also an Ultimate Beneficial Owner</label>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  <button onClick={addPerson}
                    className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-[#6B7280] transition"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.color = ACCENT_DEEP; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }}>
                    + Add {ROLE_LABEL[peopleRole]}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Fixed save bar */}
      <div className="fixed bottom-0 md:left-64 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-3 z-20">
          {saved && (
            <span className="text-sm text-green-600 font-medium flex items-center gap-1">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Saved
            </span>
          )}
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-[#374151] border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50"
            style={{ background: ACCENT }}
            onMouseEnter={e => { e.currentTarget.style.background = ACCENT_DEEP; }}
            onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}>
            {loading ? 'Saving…' : 'Save changes'}
          </button>
      </div>
    </AppShell>
  );
}
